// Bound the `?apiDomain=` config param to hosts we own.
//
// ⚠️ This value is NOT operator-controlled. The loader builds the iframe URL
// from the station's `data-config` AND the host page's own query string —
// `classes/widget.js formatUrlForIframe` appends `document.location.search`
// verbatim — so any visitor who can get someone to open
// `https://station.example/page?apiDomain=…` chooses the host the widget talks
// to. Measured against the deployed build before this guard existed:
// `apiDomain=attacker.example` sent both the playlist fetch and the Faro
// collector POST (page_url, session id, user attributes) to that host.
//
// Every consumer reads the sanitized value from ONE seam in app.vue
// (`processQueryParams`), so this bounds the playlist API, the Faro collector,
// image URLs, and the v4 media links (show-metadata, RSS, download) together.
// The media links are why it matters more now than it did: two of them are
// `window.open()` targets, which turns content injection into user-initiated
// NAVIGATION to a host of the attacker's choosing.
//
// The allowlist itself lives in config/hosts.js as TRUSTED_API_DOMAIN: that file
// is the designated single home for host knowledge, and its centralization guard
// caught this pattern when it lived here.
import { TRUSTED_API_DOMAIN } from '../config/hosts';

/**
 * Return `raw` when it is a hostname we own, otherwise `fallback`.
 *
 * Falls back silently rather than throwing: a widget that refuses to render is
 * worse for the station than one that renders against the default API. The
 * rejection is surfaced on the console so a legitimate new cluster shows up as
 * a diagnosable warning instead of a mysterious wrong-host render.
 *
 * @param {string} raw       the `?apiDomain=` value, or undefined
 * @param {string} fallback  the default host to use when `raw` is absent/untrusted
 * @returns {string}
 */
export const sanitizeApiDomain = (raw, fallback) => {
  if (!raw || typeof raw !== 'string') return fallback;
  if (TRUSTED_API_DOMAIN.test(raw)) return raw;
  // eslint-disable-next-line no-console
  console.warn(`widget: ignoring untrusted apiDomain "${raw}" — using ${fallback}`);
  return fallback;
};
